import { ApiDef_OpenTelemetry } from '@app/open-telemetry-shared/apis';
import { apiWithBody, ModuleFE_XHR } from '@nu-art/thunderstorm-frontend/index';
import { Module } from '@nu-art/ts-common';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { context, propagation, trace } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { CoralogixTransactionSampler } from '@coralogix/opentelemetry';
import { AlwaysOnSampler } from '@opentelemetry/sdk-trace-base';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { FeToBeExporter } from './custom-components/CustomExporter.js';
export class ModuleFE_OpenTelemetry_Class extends Module {
    initialized = false;
    v1;
    constructor() {
        super();
        this.v1 = {
            sendTelemetry: apiWithBody(ApiDef_OpenTelemetry.v1.sendTelemetry),
            triggerTest: apiWithBody(ApiDef_OpenTelemetry.v1.triggerTest),
        };
        this.setDefaultConfig({
            timeout: 15000,
            exporterConfig: {
                serviceName: 'advisor-ts-fe',
                applicationName: 'default-ts-application-name',
                subSystemName: 'default-ts-service-name',
                url: 'https://ingress.cx498.coralogix.com:443/v1/traces',
            },
            processorConfig: {
                maxExportBatchSize: 50,
                scheduledDelayMillis: 5000,
                exportTimeoutMillis: 30000,
                maxQueueSize: 100,
            },
        });
    }
    /**
     * To be initialized in the app module
     */
    initializeOTel = (serviceName) => {
        const NotTracing = true;
        if (NotTracing)
            return;
        const sampler = new CoralogixTransactionSampler(new AlwaysOnSampler());
        const exporter = new FeToBeExporter();
        const provider = new WebTracerProvider({
            resource: resourceFromAttributes({
                [ATTR_SERVICE_NAME]: serviceName ?? this.config.exporterConfig.serviceName,
                'cx.application.name': this.config.exporterConfig.applicationName,
                'cx.subsystem.name': this.config.exporterConfig.subSystemName,
            }),
            sampler: sampler,
            spanProcessors: [new BatchSpanProcessor(exporter, {
                    maxExportBatchSize: this.config.processorConfig.maxExportBatchSize,
                    scheduledDelayMillis: this.config.processorConfig.scheduledDelayMillis,
                    exportTimeoutMillis: this.config.processorConfig.exportTimeoutMillis,
                    maxQueueSize: this.config.processorConfig.maxQueueSize,
                })],
        });
        provider.register({
            propagator: new W3CTraceContextPropagator(),
            contextManager: new ZoneContextManager()
        });
        // todo test and determine if useful or overkill/not specific enough
        registerInstrumentations({ instrumentations: [new XMLHttpRequestInstrumentation()] });
        // @ts-ignore // The headers are unknown until we have NotTracing trace. Adding here headers that are dynamically created by NotTracing contextual function.
        const getDefaultHeadersOrigin = ModuleFE_XHR.getDefaultHeaders;
        // @ts-ignore
        ModuleFE_XHR.getDefaultHeaders = () => {
            const originalDefaultHeaders = getDefaultHeadersOrigin.bind(ModuleFE_XHR)();
            const propagationHeaders = this.getPropagationHeaders();
            return { ...originalDefaultHeaders, ...propagationHeaders };
        };
        this.initialized = true;
    };
    getTracer = (name = 'default-frontend-tracer') => {
        return trace.getTracer(name);
    };
    getPropagationHeaders = () => {
        // Extract the current context and inject it into headers
        const headers = {};
        propagation.inject(context.active(), headers);
        this.logWarning('Propagation Headers:', headers);
        return headers;
    };
}
export const ModuleFE_OpenTelemetry = new ModuleFE_OpenTelemetry_Class();
export const SpanRuntimeFE = {
    /**
     * Need to think of how to grab the span and pass it along or hold it, so it'll function as parent span instead of level 1 spanning.
     * @param name The name of the span
     * @param functionToSpan The function to wrap in a span
     */
    wrapFunctionWithSpan: async (name, functionToSpan) => {
        if (!ModuleFE_OpenTelemetry.initialized)
            return functionToSpan();
        const tracer = ModuleFE_OpenTelemetry.getTracer();
        const parentSpan = trace.getActiveSpan();
        const parentContext = parentSpan ? trace.setSpan(context.active(), parentSpan) : context.active();
        const span = tracer.startSpan(name, undefined, parentContext);
        try {
            const ctxWithSpan = trace.setSpan(parentContext, span);
            ModuleFE_OpenTelemetry.logInfoBold(`F, ${parentSpan?.spanContext().traceId ?? 'ROOT'}: ${name}`);
            span.addEvent(`${name} started`);
            const result = await context.with(ctxWithSpan, () => functionToSpan());
            span.addEvent(`${name} completed successfully`);
            return result;
        }
        catch (error) {
            // Record the error in the span
            span.recordException(error);
            span.setStatus({ code: 2 });
            throw error;
        }
        finally {
            span.end();
        }
    },
    wrapPromiseWithSpan: async function (name, promiseToSpan) {
        if (!ModuleFE_OpenTelemetry.initialized)
            return promiseToSpan;
        const tracer = ModuleFE_OpenTelemetry.getTracer();
        const parentSpan = trace.getActiveSpan();
        const parentContext = parentSpan ? trace.setSpan(context.active(), parentSpan) : context.active();
        const span = tracer.startSpan(name, undefined, parentContext);
        try {
            const ctxWithSpan = trace.setSpan(parentContext, span);
            ModuleFE_OpenTelemetry.logInfoBold(`P, ${parentSpan?.spanContext().traceId ?? 'ROOT'}: ${name}`);
            span.addEvent(`${name} started`);
            const result = await context.with(ctxWithSpan, async () => await promiseToSpan);
            span.addEvent(`${name} resolved`);
            return result;
        }
        catch (error) {
            // Record the error in the span
            span.recordException(error);
            span.setStatus({ code: 2 });
            throw error;
        }
        finally {
            // End the span regardless of success or failure
            span.end();
        }
    }
};
//# sourceMappingURL=ModuleFE_OpenTelemetry.js.map